import { HoverableLink, HoverableLinkSizes } from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import SvgCheck from 'src/components/Svgs/SvgCheck'
import SvgFlag from 'src/components/Svgs/SvgFlag'
import SvgHourglass from 'src/components/Svgs/SvgHourglass'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { useTranslate } from 'src/localization/Trns'
import {
  TransactionFinalApprovalStatus,
  TransactionListModel,
} from 'src/models/transaction.models'
import { usePatchTransactionFinalApprovalStatus } from 'src/queries/expenseReview.queries'
import { usePatchTransactionReviewStatus } from 'src/queries/orgExpenseReports.queries'
import { selectSelectAllTransactionsFiltersFromState } from 'src/redux/selectors/data/transactions.selectors'
import { css } from 'styled-components'

interface UpdateTransactionStatusPopupMenuProps {
  orgExpenseReportId?: string
  transactions: TransactionListModel[]
  filters?: TransactionsFiltersBody
}

const UpdateTransactionStatusPopupMenu: React.FC<
  UpdateTransactionStatusPopupMenuProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()

  const { mutate: patchTransactionReviewStatus } =
    usePatchTransactionReviewStatus()

  const { mutate: patchTransactionFinalApprovalStatus } =
    usePatchTransactionFinalApprovalStatus()

  const selectAllTransactionsFilters = useSelector(
    selectSelectAllTransactionsFiltersFromState,
  )

  const updateStatus = (
    status: TransactionFinalApprovalStatus,
    analyticsEvent: string,
    analyticsProperties: Record<string, unknown>,
  ): void => {
    if (props.orgExpenseReportId !== undefined) {
      patchTransactionReviewStatus({
        orgExpenseReportId: props.orgExpenseReportId,
        filters: selectAllTransactionsFilters,
        body: {
          transactionIds:
            selectAllTransactionsFilters !== undefined
              ? undefined
              : props.transactions.map((txn) => txn.id),
          status: status,
        },
      })
      analytics?.track(analyticsEvent, analyticsProperties)
    } else {
      patchTransactionFinalApprovalStatus({
        filters: selectAllTransactionsFilters,
        invalidateFilters: selectAllTransactionsFilters ?? props.filters,
        body: {
          transactionIds: props.transactions.map((txn) => txn.id),
          finalApprovalStatus: status,
        },
      })
    }
  }

  const updateStatusToApproved = (): void => {
    updateStatus(
      TransactionFinalApprovalStatus.Reviewed,
      'transaction reviewed',
      {
        transaction_reviewed_count: props.transactions.filter(
          (transaction) =>
            transaction.reviewStatus !==
            TransactionFinalApprovalStatus.Reviewed,
        ).length,
        status: TransactionFinalApprovalStatus.Reviewed.toLowerCase(),
      },
    )
  }

  const updateStatusToPendingApproval = (): void => {
    updateStatus(
      TransactionFinalApprovalStatus.PendingReview,
      'transaction pending',
      {
        transaction_pending_count: props.transactions.filter(
          (transaction) =>
            transaction.reviewStatus !==
            TransactionFinalApprovalStatus.PendingReview,
        ).length,
        status: TransactionFinalApprovalStatus.PendingReview.toLowerCase(),
      },
    )
  }

  const updateStatusToFlagged = (): void => {
    updateStatus(
      TransactionFinalApprovalStatus.Flagged,
      'transaction flagged',
      {
        transaction_pending_count: props.transactions.filter(
          (transaction) =>
            transaction.reviewStatus !== TransactionFinalApprovalStatus.Flagged,
        ).length,
        status: TransactionFinalApprovalStatus.Flagged.toLowerCase(),
      },
    )
  }

  return (
    <div
      css={`
        padding: 10px 0;
      `}
    >
      <HoverableLink
        css={css`
          display: flex;
          align-items: center;
          height: 50px;
          padding-left: 15px;
        `}
        regular={true}
        size={HoverableLinkSizes.Medium}
        onClick={(): void => updateStatusToPendingApproval()}
        ariaLabelSource={'Set to Pending Approval'}
        testId={'pendingApprovalBtn'}
      >
        <SvgHourglass />
        {translate('Pending Approval')}
      </HoverableLink>
      <HoverableLink
        css={css`
          display: flex;
          align-items: center;
          height: 50px;
          padding-left: 15px;
        `}
        regular={true}
        size={HoverableLinkSizes.Medium}
        onClick={(): void => updateStatusToFlagged()}
        ariaLabelSource={'Set to Flagged'}
        testId={'flaggeBtn'}
      >
        <SvgFlag
          stroke={'var(--color-cta)'}
          css={css`
            margin: 4px;
          `}
        />
        {translate('Reject')}
      </HoverableLink>
      <HoverableLink
        css={css`
          display: flex;
          align-items: center;
          height: 50px;
          padding-left: 15px;
        `}
        regular={true}
        size={HoverableLinkSizes.Medium}
        onClick={(): void => updateStatusToApproved()}
        ariaLabelSource={'Set to Approved'}
        testId={'approveBtn'}
      >
        <SvgCheck fill={'var(--color-cta)'} />
        {translate('Approve')}
      </HoverableLink>
    </div>
  )
}

export default UpdateTransactionStatusPopupMenu
